import { useAuth, useUser } from '@clerk/clerk-react';
import { useCallback, useMemo } from 'react';

export type UserRole = 'OWNER' | 'ADMIN' | 'SUPERVISOR' | 'AGENT' | 'VIEWER';
export type PermissionScope =
  | 'CASES_VIEW'
  | 'CASE_ASSUME'
  | 'CASE_REPLY'
  | 'CASE_CLOSE'
  | 'CASE_RETURN_TO_BOT'
  | 'CASE_MARK_SPAM'
  | 'CASE_CHANGE_STATUS'
  | 'CASE_CHANGE_OUTCOME'
  | 'CONFIG_CHANGE'
  | 'CONFIG_PUBLISH'
  | 'METRICS_VIEW'
  | 'REPLAY_EXECUTE'
  | 'USERS_ADMIN'
  | 'PRIVACY_MANAGE';

type TenantMembership = {
  tenant_id: string;
  role: UserRole;
};

const ROLE_SCOPES: Record<UserRole, readonly PermissionScope[]> = {
  OWNER: [
    'CASES_VIEW',
    'CASE_ASSUME',
    'CASE_REPLY',
    'CASE_CLOSE',
    'CASE_RETURN_TO_BOT',
    'CASE_MARK_SPAM',
    'CASE_CHANGE_STATUS',
    'CASE_CHANGE_OUTCOME',
    'CONFIG_CHANGE',
    'CONFIG_PUBLISH',
    'METRICS_VIEW',
    'REPLAY_EXECUTE',
    'USERS_ADMIN',
    'PRIVACY_MANAGE',
  ],
  ADMIN: [
    'CASES_VIEW',
    'CASE_ASSUME',
    'CASE_REPLY',
    'CASE_CLOSE',
    'CASE_RETURN_TO_BOT',
    'CASE_MARK_SPAM',
    'CASE_CHANGE_STATUS',
    'CASE_CHANGE_OUTCOME',
    'CONFIG_CHANGE',
    'CONFIG_PUBLISH',
    'METRICS_VIEW',
    'REPLAY_EXECUTE',
    'USERS_ADMIN',
    'PRIVACY_MANAGE',
  ],
  SUPERVISOR: [
    'CASES_VIEW',
    'CASE_ASSUME',
    'CASE_REPLY',
    'CASE_CLOSE',
    'CASE_RETURN_TO_BOT',
    'CASE_MARK_SPAM',
    'CASE_CHANGE_STATUS',
    'CASE_CHANGE_OUTCOME',
    'METRICS_VIEW',
    'REPLAY_EXECUTE',
  ],
  AGENT: [
    'CASES_VIEW',
    'CASE_ASSUME',
    'CASE_REPLY',
    'CASE_CLOSE',
    'CASE_RETURN_TO_BOT',
    'CASE_MARK_SPAM',
  ],
  VIEWER: ['CASES_VIEW', 'METRICS_VIEW'],
};

export function roleHasScope(role: UserRole | null | undefined, scope: PermissionScope) {
  return role ? ROLE_SCOPES[role].includes(scope) : false;
}

const ROLE_ALIASES: Record<string, UserRole> = {
  OWNER: 'OWNER',
  ADMIN: 'ADMIN',
  SUPERVISOR: 'SUPERVISOR',
  MANAGER: 'SUPERVISOR',
  AGENT: 'AGENT',
  OPERATOR: 'AGENT',
  ATTENDANT: 'AGENT',
  ATENDENTE: 'AGENT',
  STAFF: 'AGENT',
  VIEWER: 'VIEWER',
  READONLY: 'VIEWER',
  READ_ONLY: 'VIEWER',
};

function normalizeUserRole(input: unknown): UserRole | null {
  if (typeof input !== 'string') return null;
  const normalized = input
    .trim()
    .replace(/^org:/i, '')
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
  return ROLE_ALIASES[normalized] ?? null;
}

function asObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function firstString(...values: unknown[]) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

function stringList(value: unknown) {
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (!Array.isArray(value)) return [];
  return value.map((item) => (typeof item === 'string' ? item.trim() : '')).filter(Boolean);
}

function pushMembership(memberships: TenantMembership[], tenant_id: unknown, roleInput: unknown) {
  const tenant = typeof tenant_id === 'string' ? tenant_id.trim() : '';
  const role = normalizeUserRole(roleInput);
  if (!tenant || !role) return;
  if (!memberships.some((membership) => membership.tenant_id === tenant)) {
    memberships.push({ tenant_id: tenant, role });
  }
}

function parseMemberships(metadata: Record<string, unknown>) {
  const memberships: TenantMembership[] = [];
  const defaultRole = firstString(metadata.role);
  const arrayInputs = [metadata.tenant_memberships, metadata.tenants];
  for (const input of arrayInputs) {
    if (!Array.isArray(input)) continue;
    for (const item of input) {
      const obj = asObject(item);
      if (typeof item === 'string') {
        const [tenant_id, role] = item.split(':');
        pushMembership(memberships, tenant_id, role ?? defaultRole);
      } else if (obj) {
        pushMembership(
          memberships,
          firstString(obj.tenant_id, obj.tenantId, obj.id, obj.slug),
          firstString(obj.role, obj.user_role, obj.membership_role) ?? defaultRole,
        );
      }
    }
  }

  for (const tenant_id of stringList(metadata.tenant_ids)) {
    pushMembership(memberships, tenant_id, defaultRole);
  }

  const tenantRoles = asObject(metadata.tenant_roles);
  if (tenantRoles) {
    for (const [tenant_id, role] of Object.entries(tenantRoles)) {
      pushMembership(memberships, tenant_id, role);
    }
  }

  pushMembership(
    memberships,
    firstString(metadata.tenant_id, metadata.tenantId),
    firstString(metadata.role),
  );

  return memberships;
}

export function useTenantContext() {
  const { user } = useUser();
  const metadata = useMemo(
    () => (user?.publicMetadata ?? {}) as Record<string, unknown>,
    [user?.publicMetadata],
  );
  const memberships = useMemo(() => parseMemberships(metadata), [metadata]);
  const preferredTenant = firstString(metadata.tenant_id, metadata.tenantId);
  const tenant_id =
    memberships.find((membership) => membership.tenant_id === preferredTenant)?.tenant_id ??
    memberships[0]?.tenant_id ??
    null;
  const role = memberships.find((membership) => membership.tenant_id === tenant_id)?.role ?? null;
  const hasScope = useCallback((scope: PermissionScope) => roleHasScope(role, scope), [role]);

  return { tenant_id, role, memberships, hasScope };
}

export function useApi() {
  const { getToken } = useAuth();
  const baseUrl = import.meta.env.VITE_API_BASE_URL as string;
  const template = (import.meta.env.VITE_CLERK_TOKEN_TEMPLATE as string) || undefined;

  const request = useCallback(
    async function request<T>(path: string, init?: RequestInit): Promise<T> {
      const token = await getToken(template ? { template } : undefined);
      if (!token) throw new Error('Missing Clerk session token');

      const res = await fetch(`${baseUrl}${path}`, {
        ...init,
        headers: {
          ...(init?.headers || {}),
          'content-type': 'application/json; charset=utf-8',
          authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`API ${res.status}: ${text}`);
      }
      return (await res.json()) as T;
    },
    [baseUrl, getToken, template],
  );

  return { request };
}
